<template>
  <div class="dashboard-container">
    <!-- 全局刷新按钮 -->
    <el-tooltip :content="$t('common.refresh')" placement="left">
      <el-button
        class="dashboard-refresh-btn"
        type="primary"
        icon="el-icon-refresh"
        circle
        size="mini"
        :loading="globalRefreshing"
        @click="refreshAll"
      ></el-button>
    </el-tooltip>
    <!-- 统计卡片 -->
    <div class="stats-row">
      <div class="stat-card-wrapper" v-for="card in statCards" :key="card.key">
        <el-card
          class="stat-card"
          :class="[card.key + '-card', { 'stat-anomaly': card.anomaly }]"
          shadow="hover"
          @click.native="handleStatCardClick(card)"
        >
          <div class="stat-content">
            <div class="stat-icon" :class="card.iconClass">
              <i :class="card.icon"></i>
            </div>
            <div class="stat-info">
              <div class="stat-number">{{ card.total }}</div>
              <div class="stat-label">{{ card.label }}</div>
            </div>
          </div>
          <div class="stat-trend-row">
            <div class="stat-trend" :class="card.trendClass">
              <i :class="card.isUp ? 'el-icon-top' : (card.isDown ? 'el-icon-bottom' : 'el-icon-minus')"></i>
              <span>{{ card.changeText }}</span>
              <span class="stat-percent">({{ card.percentText }})</span>
            </div>
            <el-tooltip v-if="card.anomaly" :content="card.anomalyReason" placement="top">
              <el-tag type="warning" size="mini" effect="dark" class="anomaly-tag">
                <i class="el-icon-warning"></i> {{ $t('dashboard.anomaly') }}
              </el-tag>
            </el-tooltip>
          </div>
        </el-card>
      </div>
    </div>

    <!-- 增长趋势图(用户/设备/智能体) -->
    <el-row :gutter="20" class="growth-row">
      <el-col :span="24">
        <el-card class="chart-card growth-chart-card" shadow="hover">
          <div slot="header" class="card-header">
            <div class="header-title-wrapper">
              <div class="title-icon-box growth-icon-box">
                <i class="el-icon-data-analysis"></i>
              </div>
              <div class="title-text-box">
                <span class="header-main-title">{{ $t('dashboard.growthTrend') }}</span>
                <div class="growth-breadcrumb" v-if="growthDrill.level !== 'overview'">
                  <el-divider direction="vertical"></el-divider>
                  <el-breadcrumb separator="/">
                    <el-breadcrumb-item>
                      <a @click.prevent="resetGrowthDrill">{{ $t('resourceManagement.overview') }}</a>
                    </el-breadcrumb-item>
                    <el-breadcrumb-item v-if="growthDrill.level === 'month' || growthDrill.level === 'day'">
                      {{ growthDrill.focusMonth }}
                    </el-breadcrumb-item>
                    <el-breadcrumb-item v-if="growthDrill.level === 'day'">
                      {{ growthDrill.focusDay }}
                    </el-breadcrumb-item>
                  </el-breadcrumb>
                  <el-button
                    type="text"
                    size="mini"
                    icon="el-icon-back"
                    @click="resetGrowthDrill"
                    class="back-btn"
                  >{{ $t('common.back') }}</el-button>
                </div>
              </div>
            </div>
            <div class="chart-controls">
              <div class="control-item">
                <el-select
                  v-model="growthDimension"
                  size="mini"
                  class="custom-select dimension-select"
                  @change="fetchGrowthTrend"
                >
                  <el-option :label="$t('dashboard.userGrowth')" value="user">
                    <i class="el-icon-user" style="margin-right: 8px; color: #667eea"></i>{{ $t('dashboard.userGrowth') }}
                  </el-option>
                  <el-option :label="$t('dashboard.deviceGrowth')" value="device">
                    <i class="el-icon-mobile-phone" style="margin-right: 8px; color: #2193b0"></i>{{ $t('dashboard.deviceGrowth') }}
                  </el-option>
                  <el-option :label="$t('dashboard.agentGrowth')" value="agent">
                    <i class="el-icon-cpu" style="margin-right: 8px; color: #11998e"></i>{{ $t('dashboard.agentGrowth') }}
                  </el-option>
                  <el-option :label="$t('dashboard.digitalCollectionGrowth')" value="digital_collection">
                    <i class="el-icon-medal" style="margin-right: 8px; color: #f59e0b"></i>{{ $t('dashboard.digitalCollectionGrowth') }}
                  </el-option>
                  <el-option :label="$t('dashboard.badgeGrowth')" value="badge">
                    <i class="el-icon-award" style="margin-right: 8px; color: #a855f7"></i>{{ $t('dashboard.badgeGrowth') }}
                  </el-option>
                </el-select>
              </div>
              <div class="control-item">
                <el-radio-group v-model="growthChartType" size="mini" @change="fetchGrowthTrend" class="custom-radio-group">
                  <el-tooltip :content="$t('resourceManagement.lineChart')" placement="top">
                    <el-radio-button label="line">
                      <i class="el-icon-data-line"></i>
                    </el-radio-button>
                  </el-tooltip>
                  <el-tooltip :content="$t('resourceManagement.barChart')" placement="top">
                    <el-radio-button label="bar">
                      <i class="el-icon-s-data"></i>
                    </el-radio-button>
                  </el-tooltip>
                </el-radio-group>
              </div>

              <div class="control-item date-picker-wrapper" v-if="growthPeriod === 'custom'">
                <el-date-picker
                  v-model="growthCustomDateRange"
                  type="daterange"
                  size="mini"
                  align="right"
                  unlink-panels
                  range-separator="-"
                  :start-placeholder="$t('common.startDate')"
                  :end-placeholder="$t('common.endDate')"
                  :picker-options="pickerOptions"
                  value-format="yyyy-MM-dd"
                  @change="handleGrowthCustomDateChange"
                  class="custom-date-picker"
                >
                </el-date-picker>
              </div>

              <div class="control-item">
                <el-select v-model="growthPeriod" size="mini" class="custom-select time-select" @change="handleGrowthPeriodChange">
                  <el-option :label="$t('common.today')" value="today"></el-option>
                  <el-option :label="$t('common.week')" value="week"></el-option>
                  <el-option :label="$t('common.month')" value="month"></el-option>
                  <el-option :label="$t('common.quarter')" value="quarter"></el-option>
                  <el-option :label="$t('common.year')" value="year"></el-option>
                  <el-option :label="$t('common.custom')" value="custom"></el-option>
                </el-select>
              </div>
            </div>
          </div>
          <div class="chart-container" v-loading="growthLoading">
            <v-chart
              ref="growthChart"
              :option="growthChartOption"
              :autoresize="true"
              height="100%"
              @click="handleGrowthChartClick"
            ></v-chart>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="20" class="charts-row">
      <!-- 资源使用趋势图 -->
      <el-col :span="16">
        <el-card class="chart-card trend-chart-card" shadow="hover">
          <div slot="header" class="card-header">
            <div class="header-title-wrapper">
              <div class="title-icon-box">
                <i class="el-icon-data-line"></i>
              </div>
              <div class="title-text-box">
                <span class="header-main-title">{{ $t('dashboard.resourceTrend') }}</span>
              </div>
            </div>
            <div class="chart-controls">
              <div class="control-item">
                <el-select 
                  v-model="selectedModelId" 
                  size="mini" 
                  filterable
                  class="custom-select model-select"
                  @change="fetchUsageTrend"
                  :placeholder="$t('resourceManagement.modelPlaceholder')"
                >
                  <el-option :label="$t('common.all')" value="all">
                    <i class="el-icon-menu" style="margin-right: 8px; color: #909399"></i>{{ $t('common.all') }}
                  </el-option>
                  <el-option
                    v-for="model in modelList"
                    :key="model.id"
                    :label="model.modelName"
                    :value="model.id"
                  >
                    <i class="el-icon-cpu" style="margin-right: 8px; color: #909399"></i>{{ model.modelName }}
                  </el-option>
                </el-select>
              </div>
              <div class="control-item">
                <el-radio-group v-model="chartType" size="mini" @change="fetchUsageTrend" class="custom-radio-group">
                  <el-tooltip :content="$t('resourceManagement.lineChart')" placement="top">
                    <el-radio-button label="line">
                      <i class="el-icon-data-line"></i>
                    </el-radio-button>
                  </el-tooltip>
                  <el-tooltip :content="$t('resourceManagement.barChart')" placement="top">
                    <el-radio-button label="bar">
                      <i class="el-icon-s-data"></i>
                    </el-radio-button>
                  </el-tooltip>
                </el-radio-group>
              </div>

              <div class="control-item date-picker-wrapper" v-if="chartPeriod === 'custom'">
                <el-date-picker
                  v-model="customDateRange"
                  type="daterange"
                  size="mini"
                  align="right"
                  unlink-panels
                  range-separator="-"
                  :start-placeholder="$t('common.startDate')"
                  :end-placeholder="$t('common.endDate')"
                  :picker-options="pickerOptions"
                  value-format="yyyy-MM-dd"
                  @change="handleCustomDateChange"
                  class="custom-date-picker"
                >
                </el-date-picker>
              </div>

              <div class="control-item">
                <el-select v-model="chartPeriod" size="mini" class="custom-select time-select" @change="handlePeriodChange">
                  <el-option :label="$t('common.today')" value="today"></el-option>
                  <el-option :label="$t('common.week')" value="week"></el-option>
                  <el-option :label="$t('common.month')" value="month"></el-option>
                  <el-option :label="$t('common.quarter')" value="quarter"></el-option>
                  <el-option :label="$t('common.year')" value="year"></el-option>
                  <el-option :label="$t('common.custom')" value="custom"></el-option>
                </el-select>
              </div>
            </div>
          </div>
          <div class="chart-container" v-loading="usageTrendLoading">
            <v-chart
              ref="trendChart"
              :option="trendChartOption"
              :autoresize="true"
              height="100%"
            ></v-chart>
          </div>
        </el-card>
      </el-col>
      
      <!-- 设备状态分布 -->
      <el-col :span="8">
        <el-card class="chart-card device-status-card" shadow="hover">
          <div slot="header" class="card-header">
            <div class="header-title-wrapper">
              <div class="title-icon-box device-status-icon">
                <i class="el-icon-pie-chart"></i>
              </div>
              <div class="title-text-box">
                <span class="header-main-title">{{ $t('dashboard.deviceStatus') }}</span>
              </div>
            </div>
            <div class="chart-controls">
              <el-radio-group v-model="activeDeviceTab" size="mini" class="custom-radio-group">
                <el-radio-button label="activation">{{ $t('device.activationState') }}</el-radio-button>
                <el-radio-button label="binding">{{ $t('device.bindingStatus') }}</el-radio-button>
                <el-radio-button label="running">{{ $t('device.onlineStatus') }}</el-radio-button>
                <el-radio-button label="version">{{ $t('dashboard.deviceVersion') }}</el-radio-button>
              </el-radio-group>
            </div>
          </div>
          <div class="chart-container tab-content-container">
            <div v-if="activeDeviceTab === 'activation'" class="tab-pane-content">
              <PieChart :data="deviceActivationChartData" height="260px" donut donut-radius="40%" />
            </div>
            <div v-if="activeDeviceTab === 'binding'" class="tab-pane-content">
              <PieChart :data="deviceBindingChartData" height="260px" donut donut-radius="40%" />
            </div>
            <div v-if="activeDeviceTab === 'running'" class="tab-pane-content">
              <PieChart :data="deviceRunningChartData" height="260px" donut donut-radius="40%" />
            </div>
            <div v-if="activeDeviceTab === 'version'" class="tab-pane-content">
              <PieChart :data="deviceVersionChartData" height="260px" donut donut-radius="40%" />
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="20" class="bottom-row">
      <!-- 最近活动 -->
      <el-col :span="12">
        <el-card class="activity-card" shadow="hover">
          <div slot="header">
            <div class="header-title-wrapper">
              <div class="title-icon-box activity-icon-box">
                <i class="el-icon-time"></i>
              </div>
              <div class="title-text-box">
                <span class="header-main-title">{{ $t('dashboard.recentActivities') }}</span>
              </div>
            </div>
            <el-button type="text" @click="$router.push('/system/log')">{{ $t('dashboard.viewMore') }}</el-button>
          </div>
          <div class="activity-list" v-loading="logsLoading">
            <template v-if="recentActivities.length > 0">
              <div
                v-for="activity in recentActivities"
                :key="activity.id"
                class="activity-item"
              >
                <div class="activity-icon" :class="getActivityType(activity.type)">
                  <i :class="getActivityIcon(activity.type)"></i>
                </div>
                <div class="activity-content">
                  <div class="activity-header">
                    <span class="activity-user">{{ activity.username || $t('layout.admin') }}</span>
                    <span class="activity-action">{{ $t('system.logEnums.action.' + activity.action) }}</span>
                    <el-tag v-if="activity.status === 0" size="mini" type="danger" effect="plain">{{ $t('common.fail') }}</el-tag>
                  </div>
                  <div class="activity-detail" v-if="activity.detail">{{ activity.detail }}</div>
                  <div class="activity-footer">
                    <span class="activity-time"><i class="el-icon-time"></i> {{ formatTime(activity.createDate) }}</span>
                    <span class="activity-ip" v-if="activity.ip"><i class="el-icon-location-outline"></i> {{ activity.ip }}</span>
                  </div>
                </div>
              </div>
            </template>
            <el-empty v-else :description="$t('common.noData')" :image-size="60"></el-empty>
          </div>
        </el-card>
      </el-col>
      
      <!-- 待处理告警 -->
      <el-col :span="12">
        <el-card class="status-card" shadow="hover">
          <div slot="header">
            <div class="header-title-wrapper">
              <div class="title-icon-box alert-icon-box">
                <i class="el-icon-warning-outline"></i>
              </div>
              <div class="title-text-box">
                <span class="header-main-title">{{ $t('dashboard.pendingAlerts') }}</span>
              </div>
            </div>
            <el-button type="text" @click="$router.push('/system/alarm')">{{ $t('dashboard.viewMore') }}</el-button>
          </div>
          <div class="status-list" v-loading="alertsLoading">
            <template v-if="alerts && alerts.length > 0">
                <div
                  v-for="alert in alerts.slice(0, 5)"
                  :key="alert.id"
                  class="status-item"
                  :class="'level-' + (alert.alertLevel ? alert.alertLevel.toLowerCase() : 'info')"
                >
                  <div class="service-name">
                    <div class="alert-info-top">
                      <el-tag :type="alert.category === 'SYSTEM' ? 'warning' : 'info'" size="mini" effect="plain" class="category-tag">
                        {{ $t('system.' + alert.category) }}
                      </el-tag>
                      <el-tag :type="getLevelType(alert.alertLevel)" size="mini" effect="dark" class="level-tag">
                        {{ $t('system.' + (alert.alertLevel ? alert.alertLevel.toUpperCase() : 'INFO')) }}
                      </el-tag>
                      <span class="alert-title-text">{{ alert.title || alert.content }}</span>
                    </div>
                    <div class="alert-info-bottom">
                      <span class="service-uptime"><i class="el-icon-time"></i> {{ formatTime(alert.createDate) }}</span>
                      <span class="alert-type-label" :class="'type-' + (alert.alertType ? alert.alertType.toLowerCase() : 'default')">{{ $t('system.' + alert.alertType) || alert.alertType }}</span>
                    </div>
                  </div>
                  <div class="service-status">
                    <div class="action-buttons">
                      <el-button type="text" size="mini" @click="handleLogDetail(alert)">{{ $t('common.detail') }}</el-button>
                      <el-divider v-if="alert.status === 0" direction="vertical"></el-divider>
                      <el-button v-if="alert.status === 0" type="text" size="mini" class="resolve-btn" @click="handleResolve(alert)">{{ $t('system.resolve') }}</el-button>
                    </div>
                  </div>
                </div>
            </template>
            <el-empty v-else :description="$t('common.noData')" :image-size="60"></el-empty>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 快捷操作 -->
    <el-card class="quick-actions" shadow="hover">
      <div slot="header" class="card-header">
        <div class="header-title-wrapper">
          <div class="title-icon-box quick-actions-icon-box">
            <i class="el-icon-menu"></i>
          </div>
          <div class="title-text-box">
            <span class="header-main-title">{{ $t('dashboard.quickActions') }}</span>
          </div>
        </div>
        <el-button type="text" icon="el-icon-setting" @click="openCustomizeDialog">
          {{ $t('dashboard.customize') }}
        </el-button>
      </div>
      <div class="action-grid">
        <div 
          v-for="action in displayQuickActions" 
          :key="action.id" 
          class="action-item" 
          @click="handleQuickActionClick(action)"
        >
          <div class="action-icon" :class="action.id">
            <i :class="action.icon"></i>
          </div>
          <div class="action-text">{{ $t('dashboard.' + action.langKey) }}</div>
        </div>
      </div>
    </el-card>

    <!-- 自定义快捷操作对话框 -->
    <el-dialog
      :title="$t('dashboard.customizeQuickActions')"
      :visible.sync="showCustomizeDialog"
      width="600px"
      append-to-body
      class="customize-dialog"
    >
      <div class="customize-tip">{{ $t('dashboard.selectQuickActions') }}</div>
      <el-checkbox-group v-model="tempSelectedActionIds" :max="8">
        <div class="all-actions-grid">
          <div v-for="action in allQuickActions" :key="action.id" class="customize-item">
            <el-checkbox :label="action.id">
              <div class="customize-item-content">
                <i :class="action.icon"></i>
                <span>{{ $t('dashboard.' + action.langKey) }}</span>
              </div>
            </el-checkbox>
          </div>
        </div>
      </el-checkbox-group>
      <div class="customize-footer">
        <span class="selection-count">{{ tempSelectedActionIds.length }} / 8</span>
      </div>
      <div slot="footer">
        <el-button @click="showCustomizeDialog = false" size="small">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" @click="saveQuickActions" size="small">{{ $t('common.confirm') }}</el-button>
      </div>
    </el-dialog>

    <!-- 告警日志详情对话框 -->
    <el-dialog :title="$t('system.alarmDetail')" :visible.sync="logDetailVisible" width="600px" append-to-body>
      <el-descriptions :column="1" border>
        <el-descriptions-item :label="$t('system.alarmTitle')">{{ detailLogInfo.title }}</el-descriptions-item>
        <el-descriptions-item :label="$t('system.alarmLevel')">
          <el-tag :type="getLevelType(detailLogInfo.alertLevel)">
            {{ detailLogInfo.alertLevel ? $t('system.' + detailLogInfo.alertLevel.toUpperCase()) : '-' }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item :label="$t('system.alarmCategory')">
          <el-tag :type="detailLogInfo.category === 'SYSTEM' ? 'warning' : 'info'" size="mini">
            {{ detailLogInfo.category ? $t('system.' + detailLogInfo.category) : '-' }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item :label="$t('system.alarmType')">
          {{ detailLogInfo.alertType ? ($t('system.' + detailLogInfo.alertType) || detailLogInfo.alertType) : '-' }}
        </el-descriptions-item>
        <el-descriptions-item :label="$t('system.alarmContent')">{{ detailLogInfo.content }}</el-descriptions-item>
        <el-descriptions-item :label="$t('system.triggerTime')">{{ detailLogInfo.createDate }}</el-descriptions-item>
        <el-descriptions-item :label="$t('common.status')">{{ getStatusText(detailLogInfo.status) }}</el-descriptions-item>
        <el-descriptions-item v-if="detailLogInfo.status === 2" :label="$t('system.resolveTime')">{{ detailLogInfo.resolveTime }}</el-descriptions-item>
        <el-descriptions-item :label="$t('common.remark')">{{ detailLogInfo.remark || '-' }}</el-descriptions-item>
      </el-descriptions>
      <div slot="footer">
        <el-button @click="logDetailVisible = false" size="small">{{ $t('common.close') }}</el-button>
        <el-button v-if="detailLogInfo.status === 0" type="primary" @click="handleResolve(detailLogInfo)" size="small">{{ $t('system.resolve') }}</el-button>
      </div>
    </el-dialog>

    <!-- 告警处理对话框 -->
    <el-dialog :title="$t('system.resolveAlarm')" :visible.sync="resolveVisible" width="400px" append-to-body>
      <el-form :model="resolveForm" label-width="80px" size="small">
        <el-form-item :label="$t('common.status')">
          <el-radio-group v-model="resolveForm.status">
            <el-radio :label="2">{{ $t('system.statusResolved') }}</el-radio>
            <el-radio :label="1">{{ $t('system.statusIgnored') }}</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item :label="$t('common.remark')">
          <el-input type="textarea" v-model="resolveForm.remark" rows="3" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="resolveVisible = false" size="small">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" @click="submitResolve" size="small">{{ $t('common.confirm') }}</el-button>
      </div>
    </el-dialog>

  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import * as echarts from 'echarts'
import ECharts from '@/components/charts/ECharts.vue'
import PieChart from '@/components/charts/PieChart.vue'
import request from '@/utils/request'
import { getOperationLogs } from '@/apis/system/log'
import { getDashboardStatistics, getGrowthTrend } from '@/apis/system/dashboard'
import { resolveAlarm } from '@/apis/alarm/alarm'

export default {
  name: 'Dashboard',
  components: {
    'v-chart': ECharts,
    PieChart
  },
  data() {
    return {
      chartPeriod: 'month',
      chartType: 'line',
      selectedModelId: 'all',
      activeDeviceTab: 'binding',
      growthDimension: 'user',
      growthChartType: 'line',
      growthPeriod: 'month',
      growthCustomDateRange: [],
      growthLoading: false,
      // 增长趋势下钻状态: level=overview(季/年)/month(月内每日)/day(日内小时)
      growthDrill: { level: 'overview', focusMonth: '', focusDay: '' },
      growthBucketStarts: [],
      recentActivities: [],
      logsLoading: false,
      usageTrendLoading: false,
      modelList: [],
      customDateRange: [],
      logDetailVisible: false,
      detailLogInfo: {},
      resolveVisible: false,
      resolveForm: { id: '', status: 2, remark: '' },
      showCustomizeDialog: false,
      globalRefreshing: false,
      selectedActionIds: ['create-user', 'create-agent', 'chat-history', 'bind-device'],
      tempSelectedActionIds: [],
      allQuickActions: [
        { id: 'create-user', icon: 'el-icon-circle-plus-outline', route: '/user/create', langKey: 'createUser' },
        { id: 'create-agent', icon: 'el-icon-plus', route: '/agent/create', langKey: 'createAgent' },
        { id: 'chat-history', icon: 'el-icon-chat-dot-round', route: '/agent/chat', langKey: 'chatHistory' },
        { id: 'bind-device', icon: 'el-icon-connection', route: '/device/add', langKey: 'bindDevice' },
        { id: 'quota-management', icon: 'el-icon-coin', route: '/resource/quota', langKey: 'quotaManagement' },
        { id: 'user-list', icon: 'el-icon-user', route: '/user', langKey: 'userList' },
        { id: 'device-list', icon: 'el-icon-mobile', route: '/device', langKey: 'deviceList' },
        { id: 'system-logs', icon: 'el-icon-document', route: '/system/log', langKey: 'systemLogs' },
        { id: 'notification-mgmt', icon: 'el-icon-bell', route: '/system/notification', langKey: 'notificationManagement' },
        { id: 'alarm-mgmt', icon: 'el-icon-warning-outline', route: '/system/alarm', langKey: 'alarmManagement' },
        { id: 'server-monitor', icon: 'el-icon-monitor', route: '/system/monitor', langKey: 'serverMonitor' },
        { id: 'system-config', icon: 'el-icon-setting', route: '/system/config', langKey: 'systemConfig' }
      ],
      dashboardStats: {
        user: { total: 0, trend: '0', diff: 0, isUp: true },
        device: { total: 0, trend: '0', diff: 0, isUp: true },
        agent: { total: 0, trend: '0', diff: 0, isUp: true },
        digitalCollection: { total: 0, trend: '0', diff: 0, isUp: true },
        badge: { total: 0, trend: '0', diff: 0, isUp: true },
        alert: { total: 0, trend: '0', diff: 0, isUp: true },
        deviceDistribution: {
          binding: { bound: 0, unbound: 0 },
          activation: { activated: 0, unactivated: 0 },
          running: { online: 0, offline: 0, abnormal: 0 }
        }
      },
      pickerOptions: {
        shortcuts: [{
          text: '最近一周',
          onClick(picker) {
            const end = new Date();
            const start = new Date();
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 7);
            picker.$emit('pick', [start, end]);
          }
        }, {
          text: '最近一个月',
          onClick(picker) {
            const end = new Date();
            const start = new Date();
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 30);
            picker.$emit('pick', [start, end]);
          }
        }, {
          text: '最近三个月',
          onClick(picker) {
            const end = new Date();
            const start = new Date();
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 90);
            picker.$emit('pick', [start, end]);
          }
        }]
      },
      trendChartOption: {
        grid: { left: '30', right: '30', bottom: '60', top: '40', containLabel: true },
        xAxis: { type: 'category', boundaryGap: false, data: [] },
        yAxis: { type: 'value' },
        series: []
      },
      growthChartOption: {
        grid: { left: '30', right: '30', bottom: '60', top: '40', containLabel: true },
        xAxis: { type: 'category', boundaryGap: false, data: [] },
        yAxis: { type: 'value' },
        series: []
      }
    }
  },
  computed: {
    ...mapState(['totalUsers']),
    ...mapGetters('resource', [
      'alerts',
      'alertsTotal',
      'alertsLoading',
      'resourceUsageTrend'
    ]),
    
    usageTrendLabels() {
      if (!this.resourceUsageTrend || this.resourceUsageTrend.length === 0) return []
      return this.resourceUsageTrend.map(item => item.date || item.time)
    },
    
    lineChartData() {
      if (!this.resourceUsageTrend || this.resourceUsageTrend.length === 0) return []
      
      // 假设趋势数据包含多种指标，如 LLM, ASR, TTS 调用次数
      const metrics = ['llm', 'asr', 'tts']
      const metricNames = {
        llm: this.$t('modelConfig.llm'),
        asr: this.$t('modelConfig.asr'),
        tts: this.$t('modelConfig.tts')
      }
      
      return metrics.map(metric => ({
        name: metricNames[metric] || metric,
        data: this.resourceUsageTrend.map(item => item[metric] || 0)
      })).filter(series => series.data.some(val => val > 0))
    },
    
    deviceBindingChartData() {
      // 绑定状态: 以激活设备总数为基数, 已绑定/未绑定分布
      const dist = this.dashboardStats.deviceDistribution?.binding || { bound: 0, unbound: 0 }
      return [
        { name: this.$t('device.bound'), value: dist.bound, itemStyle: { color: '#3b82f6' } },
        { name: this.$t('device.unbound'), value: dist.unbound, itemStyle: { color: '#e2e8f0' } }
      ]
    },

    deviceActivationChartData() {
      // 激活状态: activation_time存在即已激活
      const dist = this.dashboardStats.deviceDistribution?.activation || { activated: 0, unactivated: 0 }
      return [
        { name: this.$t('device.lifecycleActivated'), value: dist.activated, itemStyle: { color: '#8b5cf6' } },
        { name: this.$t('device.unactivated'), value: dist.unactivated, itemStyle: { color: '#e2e8f0' } }
      ].filter(item => item.value > 0)
    },

    statCards() {
      const defs = [
        { key: 'user', statKey: 'user', icon: 'el-icon-user', iconClass: 'user-icon', label: this.$t('dashboard.userCount'), data: this.dashboardStats.user },
        { key: 'device', statKey: 'device', icon: 'el-icon-mobile-phone', iconClass: 'device-icon', label: this.$t('dashboard.deviceCount'), data: this.dashboardStats.device },
        { key: 'agent', statKey: 'agent', icon: 'el-icon-cpu', iconClass: 'agent-icon', label: this.$t('dashboard.agentCount'), data: this.dashboardStats.agent },
        { key: 'digitalCollection', statKey: 'digital_collection', icon: 'el-icon-medal', iconClass: 'collection-icon', label: this.$t('dashboard.digitalCollectionCount'), data: this.dashboardStats.digitalCollection },
        { key: 'badge', statKey: 'badge', icon: 'el-icon-award', iconClass: 'badge-icon', label: this.$t('dashboard.badgeCount'), data: this.dashboardStats.badge },
        { key: 'alert', statKey: 'alert', icon: 'el-icon-warning', iconClass: 'alert-icon', label: this.$t('dashboard.pendingAlerts'), data: this.dashboardStats.alert }
      ]
      return defs.map(c => {
        const d = c.data || {}
        const change = Number(d.dailyChange || 0)
        const isUp = change > 0
        const isDown = change < 0
        // 告警卡片语义反转: 上升为不利(红), 下降为有利(绿)
        let trendClass = 'stable'
        if (c.key === 'alert') {
          trendClass = change > 0 ? 'alert-up' : (change < 0 ? 'alert-down' : 'stable')
        } else {
          trendClass = change > 0 ? 'up' : (change < 0 ? 'down' : 'stable')
        }
        return {
          ...c,
          total: d.total || 0,
          change,
          isUp,
          isDown,
          changeText: this.formatChange(change),
          percentText: this.formatPercent(d.percentChange),
          trendClass,
          anomaly: !!d.anomaly,
          anomalyReason: d.anomalyReason || ''
        }
      })
    },

    displayQuickActions() {
      return this.allQuickActions.filter(action => this.selectedActionIds.includes(action.id))
    },

     deviceRunningChartData() {
       const dist = this.dashboardStats.deviceDistribution?.running || { online: 0, offline: 0, abnormal: 0 }
       return [
         { name: this.$t('common.online'), value: dist.online, itemStyle: { color: '#10b981' } },
         { name: this.$t('common.offline'), value: dist.offline, itemStyle: { color: '#94a3b8' } },
         { name: this.$t('common.abnormal'), value: dist.abnormal, itemStyle: { color: '#f43f5e' } }
       ].filter(item => item.value > 0)
     },

    deviceVersionChartData() {
      const dist = this.dashboardStats.deviceDistribution?.version || {}
      const palette = ['#667eea', '#764ba2', '#2193b0', '#6dd5ed', '#11998e', '#38ef7d', '#f093fb', '#f5576c', '#4facfe', '#43e97b', '#fa709a', '#fee140', '#30cfd0', '#330867']
      const entries = Object.entries(dist).filter(([, v]) => Number(v) > 0)
      return entries.map(([ver, count], i) => ({
        name: ver,
        value: Number(count),
        itemStyle: { color: palette[i % palette.length] }
      }))
    }
   },
  created() {
    this.loadQuickActions();
    this.loadData();
  },
  methods: {
    ...mapActions(['fetchUsers']),
    ...mapActions('resource', ['fetchAlerts', 'fetchResourceUsageTrend']),
    
    async loadData() {
      try {
        await Promise.allSettled([
          this.fetchUsers({ page: 1, limit: 1 }),
          this.fetchModels(),
          this.fetchDashboardStats(),
          this.fetchAlerts({ page: 1, limit: 5, status: 0 }),
          this.fetchUsageTrend(),
          this.fetchGrowthTrend(),
          this.fetchRecentLogs()
        ]);
      } catch (error) {
        // allSettled won't throw, but keep catch for safety
      }
    },

    // 全局刷新所有数据
    async refreshAll() {
      if (this.globalRefreshing) return;
      this.globalRefreshing = true;
      try {
        await this.loadData();
      } finally {
        this.globalRefreshing = false;
      }
    },
    
    async fetchDashboardStats() {
      try {
        const res = await getDashboardStatistics();
        if (res) {
          this.dashboardStats = res;
        }
      } catch (error) {
        console.error('Fetch dashboard statistics failed', error);
      }
    },
    
    async fetchModels() {
      try {
        const response = await this.$store.dispatch('model/fetchModelList', {
          pageNum: 1,
          pageSize: 100,
          modelType: 'LLM'
        })
        if (response && response.list) {
          this.modelList = response.list
        }
      } catch (error) {
        this.modelList = []
      }
    },

    loadQuickActions() {
      const saved = localStorage.getItem('quick_actions_v2')
      if (saved) {
        try {
          this.selectedActionIds = JSON.parse(saved)
        } catch (e) {
          console.error('Load quick actions failed', e)
        }
      }
    },

    saveQuickActions() {
      if (this.tempSelectedActionIds.length === 0) {
        this.$message.warning(this.$t('dashboard.selectQuickActions'))
        return
      }
      this.selectedActionIds = [...this.tempSelectedActionIds]
      localStorage.setItem('quick_actions_v2', JSON.stringify(this.selectedActionIds))
      this.showCustomizeDialog = false
      this.$message.success(this.$t('common.success'))
    },

    handleQuickActionClick(action) {
      if (action.route) {
        this.$router.push(action.route)
      }
    },

    openCustomizeDialog() {
      this.tempSelectedActionIds = [...this.selectedActionIds]
      this.showCustomizeDialog = true
    },
    
    async fetchUsageTrend() {
      this.usageTrendLoading = true;
      try {
        const params = {
          timeRange: this.chartPeriod,
          modelId: this.selectedModelId === 'all' ? null : this.selectedModelId,
          granularity: this.chartPeriod === 'today' ? 'hour' : (['week', 'month'].includes(this.chartPeriod) ? 'day' : 'week')
        };
        
        if (this.chartPeriod === 'custom' && this.customDateRange && this.customDateRange.length === 2) {
          params.startDate = this.customDateRange[0];
          params.endDate = this.customDateRange[1];
        }

        const trendData = await this.$store.dispatch('resource/fetchResourceUsageTrend', params);
        this.initTrendChart(trendData);
        this.$nextTick(() => {
          if (this.$refs.trendChart) this.$refs.trendChart.resize();
        });
      } finally {
        this.usageTrendLoading = false;
      }
    },
    
    initTrendChart(trendData) {
      const isLine = this.chartType === 'line';
      
      if (!trendData || !trendData.labels || trendData.labels.length === 0) {
        this.trendChartOption = {
          title: {
            text: this.$t('resourceManagement.noUsageData'),
            left: 'center',
            top: 'center',
            textStyle: { color: '#909399', fontSize: 14, fontWeight: 'normal' }
          },
          grid: { left: '30', right: '30', bottom: '60', top: '40', containLabel: true },
          xAxis: { type: 'category', data: [] },
          yAxis: { type: 'value' },
          series: []
        };
        return;
      }
      
      const series = [{
        name: this.$t('resourceManagement.tokensConsumed'),
        type: this.chartType,
        symbol: isLine ? 'circle' : 'none',
        symbolSize: 8,
        showSymbol: false,
        areaStyle: isLine ? {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(64, 158, 255, 0.3)' },
            { offset: 1, color: 'rgba(64, 158, 255, 0)' }
          ])
        } : null,
        data: trendData.tokens || [],
        smooth: 0.45,
        itemStyle: {
          color: isLine ? '#409EFF' : new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: '#66b1ff' },
            { offset: 1, color: '#409eff' }
          ]),
          borderRadius: [4, 4, 0, 0]
        },
        lineStyle: {
          width: 3,
          color: '#409EFF',
          shadowColor: 'rgba(64, 158, 255, 0.2)',
          shadowBlur: 10,
          shadowOffsetY: 4
        }
      }];

      this.trendChartOption = {
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          borderColor: '#f0f2f5',
          borderWidth: 1,
          padding: [12, 16],
          textStyle: { color: '#303133', fontSize: 13 },
          extraCssText: 'box-shadow: 0 4px 16px rgba(0,0,0,0.08); border-radius: 8px;',
          formatter: (params) => {
            let res = `<div style="font-weight: 600; margin-bottom: 8px; color: #303133; font-size: 14px;">${params[0].name}</div>`;
            params.forEach(item => {
              res += `<div style="display: flex; align-items: center; justify-content: space-between; gap: 24px;">
                <span style="display: flex; align-items: center; color: #606266;">
                  <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${item.color}; margin-right: 8px;"></span>
                  ${item.seriesName}
                </span>
                <span style="font-weight: 600; color: #303133">${this.formatNumber(item.value)}</span>
              </div>`;
            });
            return res;
          }
        },
        legend: {
          show: true,
          top: '0',
          right: '25',
          icon: 'circle',
          itemWidth: 8,
          textStyle: { fontSize: 12, color: '#909399' }
        },
        grid: {
          left: '10',
          right: '25',
          bottom: '10',
          top: '50',
          containLabel: true
        },
        xAxis: [{
          type: 'category',
          boundaryGap: !isLine,
          data: trendData.labels || [],
          axisLabel: {
            color: '#909399',
            fontSize: 11,
            interval: 'auto',
            margin: 12,
            rotate: trendData.labels.length > 10 ? 30 : 0
          },
          axisLine: { lineStyle: { color: '#f0f2f5' } },
          axisTick: { show: false }
        }],
        yAxis: [{
          type: 'value',
          axisLabel: {
            color: '#909399',
            fontSize: 11,
            margin: 12,
            formatter: (value) => this.formatNumber(value)
          },
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: {
            lineStyle: {
              type: 'solid',
              color: '#f5f7fa'
            }
          }
        }],
        series: series
      };
    },
    
    formatNumber(num) {
      if (!num) return '0'
      if (num >= 1000000) return (num / 1000000).toFixed(2) + 'M'
      if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
      return num.toString()
    },

    formatChange(val) {
      const n = Number(val || 0)
      return (n > 0 ? '+' : '') + n
    },

    formatPercent(val) {
      const n = Number(val || 0)
      const sign = n > 0 ? '+' : ''
      return sign + n.toFixed(2) + '%'
    },

    handleStatCardClick(card) {
      // user/device/agent: 跳转增长趋势图并切换维度
      if (card.statKey !== 'alert') {
        this.growthDimension = card.statKey
        this.fetchGrowthTrend()
        this.$nextTick(() => {
          const el = this.$el.querySelector('.growth-chart-card')
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        })
      }
      // alert 维度不再支持点击交互
    },
    
    async fetchRecentLogs() {
      this.logsLoading = true;
      try {
        const res = await getOperationLogs({ page: 1, limit: 10 });
        this.recentActivities = res.list || (res.data && res.data.list) || res || [];
      } catch (error) {
        this.recentActivities = [];
      } finally {
        this.logsLoading = false;
      }
    },
    
    handlePeriodChange() {
      if (this.chartPeriod !== 'custom') {
        this.fetchUsageTrend();
      }
    },
    
    handleCustomDateChange(val) {
      if (val && val.length === 2) {
        this.fetchUsageTrend();
      }
    },

    // ===== 增长趋势图 =====
    async fetchGrowthTrend() {
      this.growthLoading = true;
      try {
        const params = this.buildGrowthTrendParams();
        const data = await getGrowthTrend(params);
        this.growthBucketStarts = (data && data.bucketStarts) || [];
        this.initGrowthChart(data);
        this.$nextTick(() => {
          if (this.$refs.growthChart) this.$refs.growthChart.resize();
        });
      } catch (error) {
        console.error('Fetch growth trend failed', error);
        this.initGrowthChart({ labels: [], growth: [] });
      } finally {
        this.growthLoading = false;
      }
    },

    // 构建增长趋势请求参数(含下钻逻辑)
    buildGrowthTrendParams() {
      const params = {
        dimension: this.growthDimension,
        timeRange: this.growthPeriod
      };
      // 下钻: month 级别(查看某月每日)
      if (this.growthDrill.level === 'month' && this.growthDrill.focusMonth) {
        const [y, m] = this.growthDrill.focusMonth.split('-').map(n => parseInt(n));
        const start = new Date(y, m - 1, 1);
        const end = new Date(y, m, 0); // 月末
        params.timeRange = 'custom';
        params.granularity = 'day';
        params.startDate = this.formatYmd(start);
        params.endDate = this.formatYmd(end);
        return params;
      }
      // 下钻: day 级别(查看某日小时)
      if (this.growthDrill.level === 'day' && this.growthDrill.focusDay) {
        params.timeRange = 'custom';
        params.granularity = 'hour';
        params.startDate = this.growthDrill.focusDay;
        params.endDate = this.growthDrill.focusDay;
        return params;
      }
      // 概览级
      let granularity;
      if (this.growthPeriod === 'today') {
        granularity = 'hour';
      } else if (['week', 'month'].includes(this.growthPeriod)) {
        granularity = 'day';
      } else if (['quarter', 'year'].includes(this.growthPeriod)) {
        granularity = 'month';
      } else if (this.growthPeriod === 'custom' && this.growthCustomDateRange && this.growthCustomDateRange.length === 2) {
        const start = new Date(this.growthCustomDateRange[0]);
        const end = new Date(this.growthCustomDateRange[1]);
        const diffDays = Math.round((end - start) / (1000 * 60 * 60 * 24));
        granularity = diffDays === 0 ? 'hour' : (diffDays <= 60 ? 'day' : 'month');
      } else {
        granularity = 'day';
      }
      params.granularity = granularity;
      if (this.growthPeriod === 'custom' && this.growthCustomDateRange && this.growthCustomDateRange.length === 2) {
        params.startDate = this.growthCustomDateRange[0];
        params.endDate = this.growthCustomDateRange[1];
      }
      return params;
    },

    formatYmd(d) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },

    initGrowthChart(growthData) {
      const isLine = this.growthChartType === 'line';
      // 维度配色(与统计卡片渐变保持一致)
      const dimColors = {
        user: { line: '#667eea', barTop: '#667eea', barBottom: '#764ba2', area: 'rgba(102, 126, 234, 0.3)' },
        device: { line: '#2193b0', barTop: '#6dd5ed', barBottom: '#2193b0', area: 'rgba(33, 147, 176, 0.3)' },
        agent: { line: '#11998e', barTop: '#38ef7d', barBottom: '#11998e', area: 'rgba(17, 153, 142, 0.3)' },
        digital_collection: { line: '#f59e0b', barTop: '#fbbf24', barBottom: '#f59e0b', area: 'rgba(245, 158, 11, 0.3)' },
        badge: { line: '#a855f7', barTop: '#c084fc', barBottom: '#a855f7', area: 'rgba(168, 85, 247, 0.3)' }
      };
      const color = dimColors[this.growthDimension] || dimColors.user;
      const dimensionLabel = this.growthDimension === 'device'
        ? this.$t('dashboard.deviceGrowth')
        : (this.growthDimension === 'agent' ? this.$t('dashboard.agentGrowth')
          : (this.growthDimension === 'digital_collection' ? this.$t('dashboard.digitalCollectionGrowth')
            : (this.growthDimension === 'badge' ? this.$t('dashboard.badgeGrowth')
              : this.$t('dashboard.userGrowth'))));

      if (!growthData || !growthData.labels || growthData.labels.length === 0) {
        this.growthChartOption = {
          title: {
            text: this.$t('resourceManagement.noUsageData'),
            left: 'center',
            top: 'center',
            textStyle: { color: '#909399', fontSize: 14, fontWeight: 'normal' }
          },
          grid: { left: '30', right: '30', bottom: '60', top: '40', containLabel: true },
          xAxis: { type: 'category', data: [] },
          yAxis: { type: 'value' },
          series: []
        };
        return;
      }

      const series = [{
        name: dimensionLabel,
        type: this.growthChartType,
        symbol: isLine ? 'circle' : 'none',
        symbolSize: 8,
        showSymbol: false,
        cursor: this.isGrowthChartDrillable() ? 'pointer' : 'default',
        areaStyle: isLine ? {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: color.area },
            { offset: 1, color: color.area.replace(/0\.\d+/, '0') }
          ])
        } : null,
        data: growthData.growth || [],
        smooth: 0.45,
        itemStyle: {
          color: isLine ? color.line : new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: color.barTop },
            { offset: 1, color: color.barBottom }
          ]),
          borderRadius: [4, 4, 0, 0]
        },
        lineStyle: {
          width: 3,
          color: color.line,
          shadowColor: color.area,
          shadowBlur: 10,
          shadowOffsetY: 4
        }
      }];

      this.growthChartOption = {
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          borderColor: '#f0f2f5',
          borderWidth: 1,
          padding: [12, 16],
          textStyle: { color: '#303133', fontSize: 13 },
          extraCssText: 'box-shadow: 0 4px 16px rgba(0,0,0,0.08); border-radius: 8px;',
          formatter: (params) => {
            let res = `<div style="font-weight: 600; margin-bottom: 8px; color: #303133; font-size: 14px;">${params[0].name}</div>`;
            params.forEach(item => {
              res += `<div style="display: flex; align-items: center; justify-content: space-between; gap: 24px;">
                <span style="display: flex; align-items: center; color: #606266;">
                  <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${item.color}; margin-right: 8px;"></span>
                  ${item.seriesName}
                </span>
                <span style="font-weight: 600; color: #303133">${this.formatNumber(item.value)}</span>
              </div>`;
            });
            return res;
          }
        },
        legend: {
          show: true,
          top: '0',
          right: '60',
          icon: 'circle',
          itemWidth: 8,
          textStyle: { fontSize: 12, color: '#909399' }
        },
        dataZoom: [
          { type: 'inside', start: 0, end: 100 },
          { type: 'slider', height: 18, bottom: 8, start: 0, end: 100 }
        ],
        grid: {
          left: '10',
          right: '25',
          bottom: '45',
          top: '50',
          containLabel: true
        },
        xAxis: [{
          type: 'category',
          boundaryGap: !isLine,
          data: growthData.labels || [],
          axisLabel: {
            color: '#909399',
            fontSize: 11,
            interval: 'auto',
            margin: 12,
            rotate: growthData.labels.length > 10 ? 30 : 0
          },
          axisLine: { lineStyle: { color: '#f0f2f5' } },
          axisTick: { show: false }
        }],
        yAxis: [{
          type: 'value',
          minInterval: 1,
          axisLabel: {
            color: '#909399',
            fontSize: 11,
            margin: 12,
            formatter: (value) => this.formatNumber(value)
          },
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: {
            lineStyle: {
              type: 'solid',
              color: '#f5f7fa'
            }
          }
        }],
        series: series
      };
    },

    handleGrowthPeriodChange() {
      // 切换时间跨度时重置下钻状态
      this.growthDrill = { level: 'overview', focusMonth: '', focusDay: '' };
      if (this.growthPeriod !== 'custom') {
        this.fetchGrowthTrend();
      }
    },

    handleGrowthCustomDateChange(val) {
      this.growthDrill = { level: 'overview', focusMonth: '', focusDay: '' };
      if (val && val.length === 2) {
        this.fetchGrowthTrend();
      }
    },

    // 图表点击下钻
    handleGrowthChartClick(params) {
      if (!params || params.componentType !== 'series') return;
      const granularity = this.buildGrowthTrendParams().granularity;
      const bucketStarts = this.growthBucketStarts || [];
      const idx = params.dataIndex;
      const bucket = bucketStarts[idx];

      // month 粒度 → 下钻到该月每日
      if (granularity === 'month' && bucket) {
        this.growthDrill = { level: 'month', focusMonth: bucket, focusDay: '' };
        this.fetchGrowthTrend();
        return;
      }
      // day 粒度 → 下钻到该日小时(仅下钻场景生效，避免概览 day 粒度也触发)
      if (granularity === 'day' && this.growthDrill.level === 'month' && bucket) {
        // bucket 格式 yyyy-MM-dd
        this.growthDrill = { level: 'day', focusMonth: this.growthDrill.focusMonth, focusDay: bucket };
        this.fetchGrowthTrend();
        return;
      }
    },

    // 当前图表是否支持点击下钻
    isGrowthChartDrillable() {
      const granularity = this.buildGrowthTrendParams().granularity;
      if (granularity === 'month') return true;
      if (granularity === 'day' && this.growthDrill.level === 'month') return true;
      return false;
    },

    // 重置下钻回到概览
    resetGrowthDrill() {
      this.growthDrill = { level: 'overview', focusMonth: '', focusDay: '' };
      this.fetchGrowthTrend();
    },
    
    getActivityType(type) {
      const typeMap = {
        'operation': 'user',
        'login': 'user',
        'error': 'system',
        'system': 'system'
      }
      return typeMap[type] || 'system'
    },
    
    getActivityIcon(type) {
      const iconMap = {
        'operation': 'el-icon-edit',
        'login': 'el-icon-key',
        'error': 'el-icon-warning-outline',
        'system': 'el-icon-setting'
      }
      return iconMap[type] || 'el-icon-info'
    },
    
    getAlertLevelLabel(level) {
      if (!level) return ''
      const l = level.toUpperCase()
      const labelMap = {
        'CRITICAL': this.$t('resourceManagement.critical'),
        'ERROR': this.$t('resourceManagement.error'),
        'WARNING': this.$t('resourceManagement.warning'),
        'INFO': this.$t('resourceManagement.info')
      }
      return labelMap[l] || level
    },
    
    getLevelType(level) {
      if (!level) return 'info'
      const l = level.toLowerCase()
      const map = { info: 'info', warning: 'warning', error: 'danger', critical: 'danger' }
      return map[l] || 'info'
    },
    
    getStatusText(status) { 
      return { 
        0: this.$t('system.statusUnresolved'), 
        1: this.$t('system.statusIgnored'), 
        2: this.$t('system.statusResolved') 
      }[status] || status 
    },
    
    handleLogDetail(row) { 
      this.detailLogInfo = row; 
      this.logDetailVisible = true 
    },
    
    handleResolve(row) { 
      this.resolveForm = { id: row.id, status: 2, remark: '' }; 
      this.resolveVisible = true 
    },
    
    async submitResolve() {
      try {
        await resolveAlarm(this.resolveForm.id, this.resolveForm)
        this.$message.success(this.$t('common.success'))
        this.resolveVisible = false
        this.logDetailVisible = false
        this.fetchAlerts({ page: 1, limit: 5, status: 0 })
      } catch (error) {
        console.error('Resolve alarm failed', error)
      }
    },
    
    formatTime(time) {
      if (!time) return ''
      const date = new Date(time)
      const now = new Date()
      const diff = now - date
      const minutes = Math.floor(diff / (1000 * 60))
      const hours = Math.floor(diff / (1000 * 60 * 60))
      const days = Math.floor(diff / (1000 * 60 * 60 * 24))
      
      if (days > 0) return this.$t('dashboard.daysAgo', { days })
      if (hours > 0) return this.$t('dashboard.hoursAgo', { hours })
      if (minutes > 0) return this.$t('dashboard.minutesAgo', { minutes })
      return this.$t('dashboard.justNow')
    }
  }
}
</script>

<style lang="scss" scoped>
.dashboard-container {
  margin: -20px;
  padding: 20px;
  flex-shrink: 0;

  // 全局刷新按钮(固定右上角)
  .dashboard-refresh-btn {
    position: fixed;
    right: 24px;
    top: 80px;
    z-index: 100;
    box-shadow: 0 4px 12px rgba(64, 158, 255, 0.4);
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      transform: rotate(180deg) scale(1.05);
      box-shadow: 0 6px 16px rgba(64, 158, 255, 0.6);
    }
  }

  // 公共卡片头部样式
  .header-title-wrapper {
    display: flex;
    align-items: center;
    gap: 12px;

    .title-icon-box {
      width: 32px;
      height: 32px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 18px;
      background: #f0f7ff;
      color: #409eff;

      &.device-status-icon { background: #f0fff4; color: #52c41a; }
      &.activity-icon-box { background: #fff7e6; color: #fa8c16; }
      &.alert-icon-box { background: #fff1f0; color: #f5222d; }
      &.quick-actions-icon-box { background: #f0f9ff; color: #1890ff; }
      &.growth-icon-box { background: #f3f0ff; color: #667eea; }
    }

    .header-main-title {
      font-size: 16px;
      font-weight: 600;
      color: #303133;
    }

    .growth-breadcrumb {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-left: 4px;

      ::v-deep .el-breadcrumb {
        font-size: 13px;
        line-height: 1.5;

        .el-breadcrumb__item {
          a { color: #409eff; cursor: pointer; font-weight: 500; }
          a:hover { color: #66b1ff; }
          .el-breadcrumb__inner { color: #303133; font-weight: 600; }
        }
      }

      .back-btn {
        padding: 2px 6px;
        font-size: 12px;
        color: #409eff;
        &:hover { color: #66b1ff; }
      }
    }
  }

  .stats-row {
    display: flex;
    gap: 20px;
    margin-bottom: 20px;

    .stat-card-wrapper {
      flex: 1;
      min-width: 0; // 防止flex子项溢出
    }

    .stat-card {
      border: none;
      border-radius: 12px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      cursor: pointer;
      transition: box-shadow 0.2s ease;

      &:hover {
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
      }

      // 异常醒目提示: 金色描边脉冲
      &.stat-anomaly {
        box-shadow: 0 0 0 2px #faad14, 0 4px 16px rgba(250, 173, 20, 0.35);
        animation: stat-anomaly-pulse 1.8s ease-in-out infinite;
      }

      &.device-card { background: linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%); }
      &.agent-card { background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%); }
      &.digitalCollection-card { background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%); }
      &.badge-card { background: linear-gradient(135deg, #a855f7 0%, #6366f1 100%); }
      &.alert-card { background: linear-gradient(135deg, #ee0979 0%, #ff6a00 100%); }

      ::v-deep .el-card__body {
        padding: 20px;
      }

      .stat-content {
        display: flex;
        align-items: center;
        margin-bottom: 12px;

        .stat-icon {
          width: 50px;
          height: 50px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 15px;
          font-size: 20px;
          background: rgba(255, 255, 255, 0.2);
        }

        .stat-info {
          .stat-number {
            font-size: 34px;
            font-weight: 700;
            margin-bottom: 5px;
            letter-spacing: -0.5px;
            text-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
          }

          .stat-label {
            font-size: 15px;
            font-weight: 500;
            opacity: 0.95;
          }
        }
      }

      .stat-trend-row {
        display: flex;
        align-items: center;
        gap: 8px;

        .stat-trend {
          display: flex;
          align-items: center;
          font-size: 13px;
          background: rgba(255, 255, 255, 0.5);
          padding: 4px 12px;
          border-radius: 6px;
          font-weight: 700;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
          flex: 1;

          &.up, &.alert-down {
            color: #01a73e;
          }

          &.down, &.alert-up {
            color: #e20e0e;
          }

          &.stable {
            color: #4b5563;
          }

          i {
            margin-right: 3px;
            font-weight: 900;
          }

          .stat-percent {
            margin-left: 4px;
            font-weight: 600;
            opacity: 0.92;
          }
        }

        .anomaly-tag {
          border: none;
          flex-shrink: 0;
          ::v-deep i { margin-right: 2px; }
        }
      }
    }
  }

  @keyframes stat-anomaly-pulse {
    0%, 100% { box-shadow: 0 0 0 2px #faad14, 0 4px 16px rgba(250, 173, 20, 0.35); }
    50% { box-shadow: 0 0 0 3px #faad14, 0 6px 22px rgba(250, 173, 20, 0.55); }
  }
  
  .charts-row, .growth-row {
    margin-bottom: 20px;

    .chart-card {
      border-radius: 12px;
      border: none;

      ::v-deep .el-card__header {
        background: #fafafa;
        border-bottom: 1px solid #ebeef5;
        padding: 0; // 这里的 padding 设为 0，因为内部有 card-header
      }

      .card-header {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .chart-controls {
          display: flex;
          align-items: center;
          gap: 12px;

          .custom-select {
            :deep(.el-input__inner) {
              background-color: #f5f7fa;
              border: 1px solid transparent;
              border-radius: 6px;
              height: 28px;
              line-height: 28px;
              font-size: 12px;
            }
            &.model-select { width: 160px; }
            &.dimension-select { width: 140px; }
            &.time-select { width: 90px; }
          }

          .date-picker-wrapper {
            :deep(.el-range-editor.el-input__inner) {
              background-color: #f5f7fa;
              border: 1px solid transparent;
              border-radius: 6px;
              height: 28px;
              padding: 0 10px;
              width: 220px;
            }
          }
        }
      }
      
      .chart-container {
        height: 300px;
        padding: 10px;
        display: flex;
        align-items: center;
        justify-content: center;

        &.tab-content-container {
          padding: 0;
          height: 300px;
          
          .tab-pane-content {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
          }
        }
      }
    }
  }
  
  .bottom-row {
    margin-bottom: 20px;
    
    .activity-card,
    .status-card {
      border-radius: 12px;
      border: none;
      height: 400px;
      display: flex;
      flex-direction: column;
      
      ::v-deep .el-card__header {
        background: #fafafa;
        border-bottom: 1px solid #ebeef5;
        padding: 15px 20px;
        flex-shrink: 0;
        
        .header-title-wrapper {
          float: left;
        }
        
        .el-button--text {
          float: right;
          padding: 0;
          line-height: 32px;
        }
      }

      ::v-deep .el-card__body {
        flex: 1;
        overflow-y: auto;
      }
      
      .activity-list {
        .activity-item {
          display: flex;
          align-items: flex-start;
          padding: 16px 0;
          border-bottom: 1px solid #f0f2f5;
          transition: all 0.3s ease;
          
          &:last-child {
            border-bottom: none;
          }

          &:hover {
            background-color: #fafafa;
            padding-left: 8px;
            padding-right: 8px;
            margin-left: -8px;
            margin-right: -8px;
            border-radius: 8px;
          }
          
          .activity-icon {
            width: 36px;
            height: 36px;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-right: 14px;
            font-size: 16px;
            flex-shrink: 0;
            
            &.user { background: #e6f7ff; color: #1890ff; }
            &.system { background: #f6ffed; color: #52c41a; }
          }
          
          .activity-content {
            flex: 1;
            min-width: 0;
            
            .activity-header {
              display: flex;
              align-items: center;
              gap: 8px;
              margin-bottom: 6px;

              .activity-user {
                font-weight: 600;
                color: #303133;
                font-size: 14px;
              }

              .activity-action {
                color: #606266;
                font-size: 14px;
              }
            }
            
            .activity-detail {
              font-size: 13px;
              color: #909399;
              margin-bottom: 8px;
              line-height: 1.5;
              background: #f8f9fb;
              padding: 6px 10px;
              border-radius: 4px;
              word-break: break-all;
            }

            .activity-footer {
              display: flex;
              align-items: center;
              gap: 16px;
              color: #c0c4cc;
              font-size: 12px;

              span {
                display: flex;
                align-items: center;
                gap: 4px;
                
                i { font-size: 13px; }
              }
            }
          }
        }
      }
      
      .status-list {
        .status-item {
          display: flex;
          align-items: center;
          padding: 14px 16px;
          border-bottom: 1px solid #f0f2f5;
          justify-content: space-between;
          transition: all 0.3s;
          border-left: 4px solid transparent;
          
          &:last-child {
            border-bottom: none;
          }

          &:hover {
            background-color: #f8fafc;
          }

          &.level-critical { border-left-color: #f56c6c; background-color: #fef2f2; }
          &.level-error { border-left-color: #f56c6c; background-color: #fef2f2; }
          &.level-warning { border-left-color: #e6a23c; background-color: #fffaf0; }
          &.level-info { border-left-color: #409eff; background-color: #f0f7ff; }

          .service-name {
            font-size: 14px;
            color: #303133;
            display: flex;
            flex-direction: column;
            gap: 8px;
            flex: 1;

            .alert-info-top {
              display: flex;
              align-items: center;
              flex-wrap: wrap;
              gap: 6px;
              
              .category-tag { margin-right: 0; border-radius: 4px; }
              .level-tag { margin-right: 0; border-radius: 4px; border: none; }

              .alert-title-text {
                font-weight: 600;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                max-width: 240px;
                color: #1e293b;
              }
            }

            .alert-info-bottom {
              display: flex;
              align-items: center;
              gap: 15px;
              font-size: 12px;
              color: #64748b;

              .service-uptime {
                display: flex;
                align-items: center;
                gap: 4px;
                i { font-size: 13px; }
              }

              .alert-type-label {
                background: #f1f5f9;
                padding: 2px 8px;
                border-radius: 4px;
                color: #475569;
                font-weight: 500;
                
                &.type-compute, &.type-llm { background-color: #e0f2fe; color: #0369a1; }
                &.type-storage, &.type-disk { background-color: #f0fdf4; color: #15803d; }
                &.type-network { background-color: #fef2f2; color: #b91c1c; }
                &.type-asr, &.type-tts { background-color: #faf5ff; color: #7e22ce; }
                &.type-system { background-color: #fff7ed; color: #9a3412; }
              }
            }
          }
          
          .service-status {
            flex-shrink: 0;
            margin-left: 15px;

            .action-buttons {
              display: flex;
              align-items: center;
              gap: 0;

              .el-button--text {
                padding: 4px 8px;
                font-weight: 600;
                
                &.resolve-btn {
                  color: #67c23a;
                  &:hover { color: #85ce61; }
                }
              }

              .el-divider--vertical {
                margin: 0 4px;
                background-color: #e2e8f0;
              }
            }
          }
        }
      }
    }
  }
  
  .quick-actions {
    border-radius: 12px;
    border: none;
    
    ::v-deep .el-card__header {
      background: #fafafa;
      border-bottom: 1px solid #ebeef5;
      padding: 0;
    }
    
    .action-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 20px;
      
      .action-item {
        text-align: center;
        padding: 20px;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.3s ease;
        
        &:hover {
          background: #f5f7fa;
          transform: translateY(-2px);
        }
        
        .action-icon {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 10px;
          font-size: 20px;
          
          &.create-user { background: #ecf5ff; color: #409EFF; }
          &.create-agent { background: #f0fff4; color: #52c41a; }
          &.chat-history { background: #fdf6ec; color: #E6A23C; }
          &.bind-device { background: #f0f9ff; color: #1890ff; }
          &.quota-management { background: #fff1f0; color: #f5222d; }
          &.user-list { background: #f9f0ff; color: #722ed1; }
          &.device-list { background: #e6fffb; color: #13c2c2; }
          &.system-logs { background: #f0f5ff; color: #2f54eb; }
          &.notification-mgmt { background: #fff7e6; color: #fa8c16; }
          &.alarm-mgmt { background: #fff2f0; color: #fa541c; }
          &.server-monitor { background: #f6ffed; color: #52c41a; }
          &.system-config { background: #f5f5f5; color: #595959; }
        }
        
        .action-text {
          font-size: 14px;
          color: #303133;
        }
      }
    }
  }
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 16px 20px;
}

.customize-dialog {
  ::v-deep .el-dialog__body {
    padding: 20px 30px;
  }
}

.customize-tip {
  margin-bottom: 20px;
  color: #64748b;
  font-size: 14px;
}

.all-actions-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
}

.customize-item {
  padding: 10px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  transition: all 0.2s;
  
  &:hover {
    border-color: #409eff;
    background-color: #f8fafc;
  }
  
  ::v-deep .el-checkbox {
    width: 100%;
    margin-right: 0;
    display: flex;
    align-items: center;
  }
  
  ::v-deep .el-checkbox__label {
    flex: 1;
    padding-left: 10px;
  }
}

.customize-item-content {
  display: flex;
  align-items: center;
  gap: 10px;
  
  i {
    font-size: 18px;
    color: #409eff;
  }
  
  span {
    font-size: 14px;
    color: #1e293b;
  }
}

.customize-footer {
  margin-top: 25px;
  text-align: right;

  .selection-count {
    font-size: 14px;
    color: #64748b;
  }
}

// 日环比明细弹窗内变化值配色
.change-up { color: #01a73e; font-weight: 700; i { margin-right: 2px; } }
.change-down { color: #e20e0e; font-weight: 700; i { margin-right: 2px; } }
.change-stable { color: #64748b; font-weight: 700; i { margin-right: 2px; } }
.text-muted { color: #c0c4cc; }

@media (max-width: 768px) {
  .all-actions-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .dashboard-container {
    .stats-row {
      flex-wrap: wrap;

      .stat-card-wrapper {
        width: calc(50% - 10px);
        flex: none;
        margin-bottom: 15px;
      }
    }

    .charts-row .el-col, .growth-row .el-col {
      margin-bottom: 15px;
      width: 100%;
    }

    // 移动端图表控件换行，保证可读性
    .charts-row .chart-controls, .growth-row .chart-controls {
      flex-wrap: wrap;
      gap: 8px;
    }

    .bottom-row .el-col {
      margin-bottom: 15px;
      width: 100%;
    }

    .quick-actions .action-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }
}
</style>